import mapboxgl from 'mapbox-gl'
import { useEffect, useRef, useState } from 'react'

import { sendToParent } from '../hooks/usePostMessage'
import { fetchPings, snapRoute } from '../services/segments'

import type { TripSegment } from '../types'

import 'mapbox-gl/dist/mapbox-gl.css'

type RawPing = { lat: number; lng: number; ts: string }

// Extend Window interface for custom icon helper
declare global {
	interface Window {
		__createColoredIcon?: (color: string, flip?: boolean) => HTMLImageElement
	}
}

interface Props {
	segments: TripSegment[]
	mapboxToken: string
	styleURL?: string
	useCustomIcon?: boolean // Toggle between circle and custom SVG icon
	token?: string // JWT for API calls
	apiURL?: string
	selectedTripId?: string | null
	onTripSelect?: (tripId: string | null) => void
	fitAllKey?: number
	viewMode?: 'map' | 'list' | 'split' // View mode to trigger resize
}

type ColorMode = 'multicolor' | 'monochrome' | 'status'

const DETAIL_VIEW_ZOOM = 7
const PRO_CLUSTER_SOURCE_ID = 'pro-labels-cluster'
const PRO_CLUSTER_LAYER_ID = 'pro-cluster-circles'
const PRO_CLUSTER_COUNT_LAYER_ID = 'pro-cluster-counts'
const PRO_UNCLUSTERED_LAYER_ID = 'pro-unclustered-labels'

type InteractionEvent = {
	type: 'click' | 'mouseenter' | 'mouseleave' | 'mousemove'
	handler: (event: mapboxgl.MapLayerMouseEvent) => void
}

type InteractionSpec = {
	layerId: string
	events: InteractionEvent[]
}

export function TruckMap({
	segments: initialSegments,
	mapboxToken,
	styleURL,
	useCustomIcon = false,
	token,
	apiURL,
	selectedTripId,
	onTripSelect: _onTripSelect,
	fitAllKey = 0,
	viewMode,
}: Props) {
	const mapContainer = useRef<HTMLDivElement>(null)
	const map = useRef<mapboxgl.Map | null>(null)
	const popup = useRef<mapboxgl.Popup | null>(null) // Reusable popup instance
	const [mapLoaded, setMapLoaded] = useState(false)
	const hasInitiallyFit = useRef(false)
	const [customIconLoaded, setCustomIconLoaded] = useState(false)
	const [iconMode, setIconMode] = useState<'circle' | 'truck'>(useCustomIcon ? 'truck' : 'circle')
	const iconModeRef = useRef<'circle' | 'truck'>(iconMode)
	const toggleButtonRef = useRef<HTMLButtonElement | null>(null)
	const [colorMode, setColorMode] = useState<ColorMode>('monochrome') // Default to monochrome
	const colorModeRef = useRef<ColorMode>(colorMode)
	const [rawPings, setRawPings] = useState<Record<string, RawPing[]>>({})
	const [segments, setSegments] = useState<TripSegment[]>(initialSegments) // Local state for segments
	const [snappingInProgress, setSnappingInProgress] = useState<Set<string>>(new Set())
	const hoveredTripIdRef = useRef<string | null>(null)
	const selectedTripIdRef = useRef<string | null>(null)
	const hoveredFeatureRef = useRef<{ source: string; id: string | number } | null>(null) // Track feature-state
	const previousTripIdsRef = useRef<string>('') // Track trip IDs to prevent unnecessary layer rebuilds
	const previousRawPingsKeysRef = useRef<string>('') // Track rawPings keys

	// Helper to set feature state for a trip
	const setTripState = (
		tripId: string,
		state: { hover?: boolean; selected?: boolean; has_selection?: boolean }
	) => {
		if (!map.current) return

		const segment = segments.find((s) => s.trip_id === tripId)
		if (!segment) return

		const sourceId = `trip-${tripId}`

		// Helper to safely set state
		const setFeatureState = (source: string, id: number, stateObj: any) => {
			if (map.current?.getSource(source)) {
				map.current.setFeatureState({ source, id }, stateObj)
			}
		}

		// Apply to marker
		setFeatureState(`${sourceId}-marker`, 0, state)

		// Apply to stops
		if (segment.meta?.stops) {
			const stopsWithCoords = segment.meta.stops.filter((s) => s.coordinates)
			stopsWithCoords.forEach((_, idx) => {
				setFeatureState(`${sourceId}-stops`, idx, state)
			})
		}

		// Apply to pings
		setFeatureState(`${sourceId}-pings`, 0, state)
	}

	// Sync segments when prop changes
	useEffect(() => {
		setSegments(initialSegments)
	}, [initialSegments])

	// Track last clicked PRO to prevent duplicate messages
	const lastClickedProRef = useRef<{ pro_num: string; timestamp: number } | null>(null)

	// Keep refs in sync with state
	useEffect(() => {
		iconModeRef.current = iconMode
		// Update button text when mode changes
		if (toggleButtonRef.current) {
			toggleButtonRef.current.textContent = iconMode === 'circle' ? 'Truck Icons' : 'Circle Icons'
		}
	}, [iconMode])

	useEffect(() => {
		colorModeRef.current = colorMode
	}, [colorMode])

	const registerInteractionSpec = (specs: InteractionSpec[]) => {
		if (!map.current) return

		specs.forEach(({ layerId, events }) => {
			events.forEach(({ type, handler }) => {
				map.current!.on(type, layerId, handler)
			})
		})
	}

	// Handle selected trip changes
	useEffect(() => {
		if (!map.current || !mapLoaded) return

		const hasSelection = !!selectedTripId

		// Update all trips: set selected state and global selection state
		segments.forEach((segment) => {
			const isSelected = selectedTripId === segment.trip_id || selectedTripId === segment.pro_num

			// Set feature state (cheap)
			setTripState(segment.trip_id, {
				selected: isSelected,
				has_selection: hasSelection,
			})
		})

		// Zoom to selected trip
		if (selectedTripId) {
			// Try finding by trip_id first
			let segment = segments.find((s) => s.trip_id === selectedTripId)

			// If not found, assume selectedTripId might be a PRO number and try finding by pro_num
			if (!segment) {
				segment = segments.find((s) => s.pro_num === selectedTripId)
			}

			if (segment) {
				const bounds = new mapboxgl.LngLatBounds()
				const [[w, s_lat], [e, n]] = segment.bounds_json
				bounds.extend([w, s_lat])
				bounds.extend([e, n])

				// Use asymmetric padding to account for the detail panel (360px) on the right
				const DETAIL_PANEL_WIDTH = 360
				map.current.fitBounds(bounds as any, {
					padding: { top: 50, bottom: 50, left: 50, right: DETAIL_PANEL_WIDTH + 50 },
					maxZoom: 12,
					duration: 1000,
					essential: true,
				})
			}
		}

		selectedTripIdRef.current = selectedTripId ?? null
	}, [selectedTripId, mapLoaded, segments])

	// Allow parent to request a full map re-fit (e.g., after closing Bubble panel)
	useEffect(() => {
		if (!map.current || !mapLoaded || !fitAllKey || !segments.length) return

		const bounds = new mapboxgl.LngLatBounds()
		segments.forEach((s) => {
			const [[w, s_lat], [e, n]] = s.bounds_json
			bounds.extend([w, s_lat])
			bounds.extend([e, n])
		})

		map.current.fitBounds(bounds as any, {
			padding: 50,
			duration: 1000,
			essential: true,
			maxZoom: 12,
		})
	}, [fitAllKey, mapLoaded, segments])

	// Fetch raw pings (always fetch for marker positioning and breadcrumbs)
	useEffect(() => {
		if (token && apiURL) {
			segments.forEach((segment) => {
				// Only fetch if we don't have them yet
				if (!rawPings[segment.trip_id]) {
					fetchPings(token, apiURL, segment.trip_id)
						.then((pings) => {
							setRawPings((prev) => ({ ...prev, [segment.trip_id]: pings }))
						})
						.catch(() => {
							// Ignore fetch errors
						})
				}
			})
		}
	}, [segments, token, apiURL]) // rawPings intentionally excluded to avoid loops

	// Helper function to check if a route is snapped (different from raw pings)
	// DEPRECATED: Keeping for future use if route lines are requested
	const _isRouteSnapped = (segment: TripSegment, pings: RawPing[]): boolean => {
		if (!pings || pings.length === 0) return false
		const geojson = segment.snapped_geojson

		if (geojson.type !== 'LineString') return false

		// If coordinates don't match in count, it's likely snapped
		if (geojson.coordinates.length !== pings.length) return true

		// Check if coordinates match exactly (within tolerance)
		const isRawRoute = geojson.coordinates.every((coord, i) => {
			const lngDiff = Math.abs(coord[0] - pings[i].lng)
			const latDiff = Math.abs(coord[1] - pings[i].lat)
			return lngDiff < 0.0001 && latDiff < 0.0001
		})

		return !isRawRoute
	}

	// Function to snap a specific route on demand
	// DEPRECATED: Keeping for future use if route lines are requested
	const _handleSnapRoute = async (tripId: string) => {
		if (!token || !apiURL || snappingInProgress.has(tripId)) return

		setSnappingInProgress((prev) => new Set(prev).add(tripId))

		try {
			const result = await snapRoute(token, apiURL, tripId)
			// Update the segment with snapped route
			setSegments((prev) =>
				prev.map((s) =>
					s.trip_id === tripId ? { ...s, snapped_geojson: result.snapped_geojson } : s
				)
			)
		} catch {
			// Ignore snap errors
		} finally {
			setSnappingInProgress((prev) => {
				const next = new Set(prev)
				next.delete(tripId)
				return next
			})
		}
	}

	useEffect(() => {
		if (!mapContainer.current) return

		mapboxgl.accessToken = mapboxToken
		map.current = new mapboxgl.Map({
			container: mapContainer.current,
			style: styleURL || 'mapbox://styles/mapbox/streets-v12',
			projection: 'mercator', // Force flat map (disable globe)
			center: [-98, 38],
			zoom: 4,
			scrollZoom: false,
		}) as unknown as mapboxgl.Map

		const controls: mapboxgl.IControl[] = []
		;(window as any).mapboxgl = mapboxgl

		// Create scroll zoom toggle control (add first so it appears at the top)
		class ScrollZoomToggle {
			_map: mapboxgl.Map | undefined
			_container: HTMLDivElement | undefined

			onAdd(map: mapboxgl.Map) {
				this._map = map
				this._container = document.createElement('div')
				this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'
				this._container.innerHTML = `
					<button
						type="button"
						title="Click to enable scroll zoom"
						style="
							padding: 5px 10px;
							font-size: 11px;
							font-weight: 600;
							white-space: nowrap;
							cursor: pointer;
							width: auto;
							height: auto;
							display: block;
						"
					>Enable Scroll Zoom</button>
				`
				const button = this._container.querySelector('button')!
				button.onclick = () => {
					const isEnabled = this._map!.scrollZoom.isEnabled()
					if (isEnabled) {
						this._map!.scrollZoom.disable()
						button.textContent = 'Enable Scroll Zoom'
						button.title = 'Click to enable scroll zoom'
					} else {
						this._map!.scrollZoom.enable()
						button.textContent = 'Disable Scroll Zoom'
						button.title = 'Click to disable scroll zoom'
					}
				}
				return this._container
			}

			onRemove() {
				this._container?.parentNode?.removeChild(this._container)
				this._map = undefined
			}
		}

		if (map.current) {
			map.current.addControl(new ScrollZoomToggle() as unknown as mapboxgl.IControl, 'top-right')
		}

		// Add navigation controls (zoom buttons and compass/reset)
		const navigationControl = new mapboxgl.NavigationControl({
			showCompass: true,
			showZoom: true,
			visualizePitch: false,
		})
		if (map.current) {
			map.current.addControl(navigationControl as unknown as mapboxgl.IControl, 'top-right')
			controls.push(navigationControl as unknown as mapboxgl.IControl)
		}

		const fullscreenControl = new mapboxgl.FullscreenControl()
		if (map.current) {
			map.current.addControl(fullscreenControl as unknown as mapboxgl.IControl, 'top-right')
			controls.push(fullscreenControl as unknown as mapboxgl.IControl)
		}

		const scaleControl = new mapboxgl.ScaleControl({
			maxWidth: 120,
			unit: 'imperial',
		})
		if (map.current) {
			map.current.addControl(scaleControl as unknown as mapboxgl.IControl, 'bottom-left')
			controls.push(scaleControl as unknown as mapboxgl.IControl)
		}

		// Create icon mode toggle control
		class IconModeToggle {
			_map: mapboxgl.Map | undefined
			_container: HTMLDivElement | undefined

			onAdd(map: mapboxgl.Map) {
				this._map = map
				this._container = document.createElement('div')
				this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'
				// Hidden for now, but not removed
				this._container.style.display = 'none'
				// Button shows what it will switch TO, not current state
				this._container.innerHTML = `
					<button
						type="button"
						title="Toggle icon style"
						style="
							padding: 5px 10px;
							font-size: 11px;
							font-weight: 600;
							white-space: nowrap;
							cursor: pointer;
							width: auto;
							height: auto;
							display: block;
						"
					>${iconMode === 'circle' ? 'Truck Icons' : 'Circle Icons'}</button>
				`
				const button = this._container.querySelector('button')!
				toggleButtonRef.current = button
				button.onclick = () => {
					// Use ref to get current state (not stale closure value)
					const currentMode = iconModeRef.current
					const newMode = currentMode === 'circle' ? 'truck' : 'circle'
					setIconMode(newMode)
				}
				return this._container
			}

			onRemove() {
				this._container?.parentNode?.removeChild(this._container)
				this._map = undefined
				toggleButtonRef.current = null
			}
		}

		if (map.current) {
			map.current.addControl(new IconModeToggle() as unknown as mapboxgl.IControl, 'top-right')
		}

		// Color Mode Selector
		class ColorModeSelector {
			_map: mapboxgl.Map | undefined
			_container: HTMLDivElement | undefined

			onAdd(map: mapboxgl.Map) {
				this._map = map
				this._container = document.createElement('div')
				this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'
				// Hidden for now, but not removed
				this._container.style.display = 'none'
				this._container.innerHTML = `
					<select
						title="Select color mode"
						style="
							padding: 5px 10px;
							font-size: 11px;
							font-weight: 600;
							cursor: pointer;
							border: none;
							background: white;
							width: auto;
							height: auto;
							border-radius: 4px;
						"
					>
						<option value="monochrome">Monochrome</option>
						<option value="multicolor">Multicolor</option>
						<option value="status">By Status</option>
					</select>
				`
				const select = this._container.querySelector('select')!
				select.value = colorMode
				select.onchange = () => {
					setColorMode(select.value as ColorMode)
				}
				return this._container
			}

			onRemove() {
				this._container?.parentNode?.removeChild(this._container)
				this._container = undefined
				this._map = undefined
			}
		}

		if (map.current) {
			map.current.addControl(new ColorModeSelector() as unknown as mapboxgl.IControl, 'top-right')
		}

		// Dynamically generate arrow icons, badge backgrounds, and stop squares for each color
		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d', { willReadFrequently: true })

		map.current.on('styleimagemissing', ({ id }) => {
			if (!ctx) return

			// Generate arrow icons
			if (id.startsWith('arrow-')) {
				const color = id.replace('arrow-', '')
				const size = 32

				ctx.canvas.width = size
				ctx.canvas.height = size
				ctx.clearRect(0, 0, size, size)

				// Draw arrow pointing up (will be rotated by bearing)
				ctx.fillStyle = color
				ctx.strokeStyle = color
				ctx.lineWidth = 1
				ctx.lineJoin = 'round'

				ctx.beginPath()
				ctx.moveTo(16, 4) // tip
				ctx.lineTo(26, 28) // bottom right
				ctx.lineTo(16, 22) // middle notch
				ctx.lineTo(6, 28) // bottom left
				ctx.closePath()
				ctx.fill()
				ctx.stroke()

				map.current?.addImage(id, ctx.getImageData(0, 0, size, size), { pixelRatio: 1 })
			}

			// Generate badge backgrounds
			if (id.startsWith('badge-')) {
				const color = id.replace('badge-', '')
				const width = 100
				const height = 24
				const radius = 4

				ctx.canvas.width = width
				ctx.canvas.height = height
				ctx.clearRect(0, 0, width, height)

				// Draw rounded rectangle
				ctx.fillStyle = color
				ctx.beginPath()
				ctx.moveTo(radius, 0)
				ctx.lineTo(width - radius, 0)
				ctx.arcTo(width, 0, width, radius, radius)
				ctx.lineTo(width, height - radius)
				ctx.arcTo(width, height, width - radius, height, radius)
				ctx.lineTo(radius, height)
				ctx.arcTo(0, height, 0, height - radius, radius)
				ctx.lineTo(0, radius)
				ctx.arcTo(0, 0, radius, 0, radius)
				ctx.closePath()
				ctx.fill()

				map.current?.addImage(id, ctx.getImageData(0, 0, width, height), {
					pixelRatio: 1,
					stretchX: [[8, width - 8]], // Allow middle to stretch
					stretchY: [[8, height - 8]],
					content: [4, 4, width - 4, height - 4], // 4px padding
				})
			}

			// Generate rounded square icons for stops
			if (id.startsWith('stop-square-')) {
				const parts = id.replace('stop-square-', '').split('-')
				// Icon IDs now use hex colors (HSL converted to hex)
				// Format: stop-square-{fillColorHex}-{borderColorHex}
				// Colors have # removed, so "#3b82f6" becomes "3b82f6"
				const fillColor = parts[0] ? `#${parts[0]}` : '#3b82f6'
				const borderColor = parts[1] ? `#${parts[1]}` : '#ffffff'

				const size = 16 // 8px radius circle = 16px diameter, so square should be similar
				const radius = 2 // Rounded corners

				ctx.canvas.width = size
				ctx.canvas.height = size
				ctx.clearRect(0, 0, size, size)

				// Draw rounded square with translucent fill and solid border
				ctx.fillStyle = fillColor
				ctx.strokeStyle = borderColor
				ctx.lineWidth = 2

				ctx.beginPath()
				ctx.moveTo(radius, 0)
				ctx.lineTo(size - radius, 0)
				ctx.arcTo(size, 0, size, radius, radius)
				ctx.lineTo(size, size - radius)
				ctx.arcTo(size, size, size - radius, size, radius)
				ctx.lineTo(radius, size)
				ctx.arcTo(0, size, 0, size - radius, radius)
				ctx.lineTo(0, radius)
				ctx.arcTo(0, 0, radius, 0, radius)
				ctx.closePath()
				ctx.fill()
				ctx.stroke()

				map.current?.addImage(id, ctx.getImageData(0, 0, size, size), { pixelRatio: 1 })
			}
		})

		// Create reusable popup instance (Best Practice: Single popup instead of creating new ones)
		popup.current = new mapboxgl.Popup({
			closeButton: true,
			closeOnClick: false,
			offset: 10,
			maxWidth: '300px',
		}) as unknown as mapboxgl.Popup

		if (map.current) {
			map.current.on('load', () => {
				setMapLoaded(true)
				sendToParent({ type: 'ready' })
			})
		}

		return () => {
			setMapLoaded(false)
			hasInitiallyFit.current = false
			setCustomIconLoaded(false)
			popup.current?.remove()
			popup.current = null
			controls.forEach((control) => {
				try {
					map.current?.removeControl(control)
				} catch {
					// Control might already be removed when map is torn down
				}
			})
			map.current?.remove()
			map.current = null
		}
	}, [mapboxToken, styleURL])

	// Resize map when view mode changes (e.g., split -> map only)
	useEffect(() => {
		if (map.current && mapLoaded && viewMode) {
			// Delay resize slightly to ensure container has finished transitioning
			const timer = setTimeout(() => {
				map.current?.resize()
			}, 100)
			return () => clearTimeout(timer)
		}
	}, [viewMode, mapLoaded])

	// Load custom SVG icon (always load so toggle works)
	useEffect(() => {
		if (!map.current || !mapLoaded) {
			return
		}

		// Truck SVG template with color placeholders
		const svgTemplate = `
			<svg xmlns="http://www.w3.org/2000/svg" width="210" height="136" viewBox="0 0 210 136" fill="none">
				<path d="M189.115 52.3536L189.124 52.3555C195.446 53.6991 205 55.7296 205 72.924V94.7042C205 101.673 199.403 107.342 192.524 107.342H174.603C172.764 120.69 161.426 131 147.749 131C133.185 131 121.27 119.308 120.66 104.704H74.8486C74.2384 119.308 62.3225 131 47.7571 131C33.1923 131 21.2773 119.308 20.667 104.704H13.1816C8.67033 104.704 5 100.986 5 96.4155V13.2886C5 8.71786 8.67033 4.99953 13.1816 5H95.5C100.011 5 103.682 8.71833 103.682 13.2886V82.2741H112.889V18.2276C112.889 14.6038 115.869 13.2883 117.872 13.2883H146.227C152.854 13.2883 160.726 18.1392 163.775 24.102L173.353 43.7467C175.916 48.7608 179.566 49.9159 186.923 51.8538C187.586 52.0285 188.329 52.1864 189.115 52.3536Z" fill="#00188F"/>
				<path d="M205 94.7042V72.924C205 56.8043 196.603 54.0123 190.344 52.6191L189.124 52.3555L189.115 52.3536C188.525 52.2282 187.96 52.1084 187.435 51.9824L186.923 51.8538C179.566 49.9159 175.916 48.7608 173.353 43.7467L163.775 24.102C160.726 18.1392 152.854 13.2883 146.227 13.2883L146.227 8.28809C150.68 8.28809 155.204 9.88378 158.966 12.2021C162.613 14.4498 165.977 17.6427 168.032 21.4551L168.227 21.8262L168.27 21.9111L177.812 41.4834C178.645 43.1102 179.486 43.893 180.709 44.5508C182.244 45.3763 184.39 46.016 188.196 47.0186L188.608 47.1221C189.042 47.2258 189.542 47.3328 190.154 47.4629L190.164 47.4648C193.185 48.1068 198.451 49.1346 202.754 52.958C207.166 56.8793 209.83 62.9751 209.992 72.0381L210 72.9238V94.7041C210 104.373 202.226 112.343 192.524 112.343H178.66C174.86 125.943 162.489 136 147.749 136C132.074 136 119.075 124.627 116.22 109.704H79.2891C76.4334 124.628 63.4327 136 47.7568 136C32.0813 136 19.082 124.627 16.2266 109.704H13.1816C5.84794 109.704 0 103.685 0 96.415V13.2881C0.000252611 6.01807 5.84761 -0.000521929 13.1816 3.39478e-08L13.1816 5L12.7607 5.01074C8.44444 5.23295 5.00024 8.86106 5 13.2886V96.4155C5 100.843 8.44428 104.471 12.7607 104.693L13.1816 104.704H20.667C21.2773 119.308 33.1923 131 47.7571 131C62.3225 131 74.2384 119.308 74.8486 104.704H120.66C121.261 119.08 132.815 130.633 147.068 130.991L147.749 131C161.426 131 172.764 120.69 174.603 107.342H192.524L192.846 107.338C199.577 107.164 205 101.564 205 94.7042ZM117.872 13.2883C115.869 13.2883 112.889 14.6038 112.889 18.2276V82.2741H103.682V13.2886C103.682 8.71833 100.011 5 95.5 5V3.39478e-08C102.834 3.39478e-08 108.681 6.0181 108.682 13.2881V14.1973C109.324 12.6835 110.322 11.4246 111.541 10.4668C113.538 8.89809 115.915 8.2881 117.872 8.28809L117.872 13.2883ZM146.227 8.28809L146.227 13.2883H117.872L117.872 8.28809H146.227ZM95.5 3.39478e-08V5H13.1816L13.1816 3.39478e-08H95.5Z" fill="{{PRIMARY_COLOR}}"/>
			</svg>
		`

		// Helper to create a colored version of the SVG (always mirror vertically)
		const createColoredIcon = (color: string, _flip: boolean = false): HTMLImageElement => {
			// Replace the placeholder with the actual color
			let coloredSvg = svgTemplate.replace(/{{PRIMARY_COLOR}}/g, color)

			// Always wrap content in a group with vertical flip transform
			// This mirrors the truck so it displays correctly on the map
			coloredSvg = coloredSvg.replace(
				'<svg xmlns="http://www.w3.org/2000/svg" width="210" height="136" viewBox="0 0 210 136" fill="none">',
				'<svg xmlns="http://www.w3.org/2000/svg" width="210" height="136" viewBox="0 0 210 136" fill="none">\n\t\t\t\t<g transform="translate(0, 136) scale(1, -1)">'
			)
			coloredSvg = coloredSvg.replace('</svg>', '\t\t\t\t</g>\n\t\t\t</svg>')

			const blob = new Blob([coloredSvg], { type: 'image/svg+xml' })
			const url = URL.createObjectURL(blob)

			const img = new Image(48, 48)
			img.src = url
			return img
		}

		// Store the helper globally for use in styleimagemissing handler
		window.__createColoredIcon = createColoredIcon

		setCustomIconLoaded(true)
	}, [mapLoaded, useCustomIcon])

	// Helper to check if two coordinates are equal within a small tolerance (approx 50 meters)
	const distanceMetersBetween = (c1: [number, number], c2: [number, number]) => {
		const R = 6371e3 // metres
		const φ1 = (c1[1] * Math.PI) / 180
		const φ2 = (c2[1] * Math.PI) / 180
		const Δφ = ((c2[1] - c1[1]) * Math.PI) / 180
		const Δλ = ((c2[0] - c1[0]) * Math.PI) / 180

		const a =
			Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
			Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
		return R * c
	}

	const areCoordsEqual = (c1: [number, number], c2: [number, number]) => {
		return distanceMetersBetween(c1, c2) < 150 // 150 meters tolerance
	}

	// Handle dynamic icon generation for custom SVG
	useEffect(() => {
		if (!map.current || !mapLoaded || !customIconLoaded) return

		const handleImageMissing = ({ id }: { id: string }) => {
			if (id.startsWith('truck-icon-') && window.__createColoredIcon) {
				// Parse: "truck-icon-hsl-60707050-flip0" (sanitized) or similar
				const match = id.match(/^truck-icon-(.+)-flip([01])$/)
				if (!match) {
					return
				}

				const colorId = match[1]
				const shouldFlip = match[2] === '1'

				// Reconstruct HSL color from sanitized ID (e.g., "hsl-60707050" -> "hsl(-60, 70%, 50%)")
				// Format: hsl[hue][saturation][lightness]
				const color = colorId.replace(/^hsl(-?\d+)(\d{2})(\d{2})$/, 'hsl($1, $2%, $3%)')

				const img = window.__createColoredIcon(color, shouldFlip)

				img.onload = () => {
					if (map.current && !map.current.hasImage(id)) {
						try {
							map.current.addImage(id, img, { pixelRatio: 1 })
						} catch {
							// Ignore image add errors
						}
					}
				}

				img.onerror = () => {
					// Ignore image load errors
				}
			} else if (id.startsWith('combo-pill-')) {
				// Format: combo-pill-{iconType}-{colorId}-{sequence}-{stopFillId}-{stopBorderId}
				// Pill contains waypoint icon placeholder on left and stop square on right
				const parts = id.split('-')
				if (parts.length < 7) return // combo, pill, iconType, color, sequence, stopFill, stopBorder

				const iconType = parts[2] // 'truck' or 'arrow'
				const _colorId = parts[3] // Kept for cache key, not used for rendering
				const sequence = parts[4]
				const stopFillId = parts[5]
				const stopBorderId = parts[6]

				const stopFill = stopFillId.startsWith('hsl')
					? stopFillId.replace(/^hsl(-?\d+)(\d{2})(\d{2})$/, 'hsl($1, $2%, $3%)')
					: `#${stopFillId}`

				const stopBorder = stopBorderId.startsWith('hsl')
					? stopBorderId.replace(/^hsl(-?\d+)(\d{2})(\d{2})$/, 'hsl($1, $2%, $3%)')
					: `#${stopBorderId}`

				// Draw pill with waypoint icon placeholder on left and stop square on right
				// Pill extends beneath waypoint icon (which is ~32px tall in circle mode, ~68px in truck mode)
				const waypointIconSize = iconType === 'truck' ? 34 : 28 // Space for waypoint icon (28 matches stop box)
				const leftPadding = 12 // Left padding
				const rightPadding = 12 // Right padding
				const stopSize = 28 // Larger stop box for better legibility
				const stopPadding = 10 // Padding around stop box
				const gapBetweenIconAndStop = 14 // Extra gap to avoid overlap with arrow circle
				const pillHeight = Math.max(waypointIconSize + 4, stopSize + stopPadding * 2) // Extend beneath waypoint
				const pillWidth =
					leftPadding + waypointIconSize + gapBetweenIconAndStop + stopSize + rightPadding
				const radius = pillHeight / 2

				const canvas = document.createElement('canvas')
				canvas.width = pillWidth
				canvas.height = pillHeight
				const ctx = canvas.getContext('2d')
				if (!ctx) return

				// Shadow
				ctx.shadowColor = 'rgba(0, 0, 0, 0.2)'
				ctx.shadowBlur = 4
				ctx.shadowOffsetY = 2

				// Pill background
				ctx.fillStyle = '#ffffff'
				ctx.beginPath()
				ctx.roundRect(0, 0, pillWidth, pillHeight, radius)
				ctx.fill()

				// Reset shadow for content
				ctx.shadowColor = 'transparent'

				// Draw waypoint icon placeholder (leave empty space; actual icon renders separately)
				const _waypointX = leftPadding
				const _waypointY = (pillHeight - waypointIconSize) / 2

				// Draw stop square on the right
				const stopX = leftPadding + waypointIconSize + gapBetweenIconAndStop
				const stopY = (pillHeight - stopSize) / 2
				const stopRadius = 4

				ctx.fillStyle = stopFill
				ctx.strokeStyle = stopBorder
				ctx.lineWidth = 2.5

				ctx.beginPath()
				ctx.roundRect(stopX, stopY, stopSize, stopSize, stopRadius)
				ctx.fill()
				ctx.stroke()

				// Draw sequence number with higher resolution
				ctx.fillStyle = '#1e40af' // Dark blue number for better contrast
				ctx.font = 'bold 20px "Open Sans", Arial' // Larger font for better legibility
				ctx.textAlign = 'center'
				ctx.textBaseline = 'middle'
				ctx.fillText(sequence, stopX + stopSize / 2, stopY + stopSize / 2 + 1)

				if (map.current && !map.current.hasImage(id)) {
					map.current.addImage(id, ctx.getImageData(0, 0, pillWidth, pillHeight), {
						pixelRatio: 2, // Higher pixel ratio for better quality
					})
				}
			}
		}

		map.current.on('styleimagemissing', handleImageMissing)

		return () => {
			map.current?.off('styleimagemissing', handleImageMissing)
		}
	}, [mapLoaded, customIconLoaded])

	// PRO number label clustering
	useEffect(() => {
		if (!map.current || !mapLoaded) {
			return
		}

		// Create clustered GeoJSON source for PRO labels
		const proClusterFeatures: GeoJSON.FeatureCollection<GeoJSON.Point> = {
			type: 'FeatureCollection',
			features: segments
				.map((segment) => {
					const pings = rawPings[segment.trip_id] || []
					const { lastCoord } = resolveMarkerCoordinates(pings, segment)

					if (!lastCoord) {
						return null
					}

					return {
						type: 'Feature',
						id: segment.trip_id,
						geometry: {
							type: 'Point',
							coordinates: lastCoord,
						},
						properties: {
							trip_id: segment.trip_id,
							pro_num: segment.pro_num,
							bounds: JSON.stringify(segment.bounds_json),
						},
					}
				})
				.filter(Boolean) as Array<GeoJSON.Feature<GeoJSON.Point>>,
		}

		const existingSource = map.current.getSource(PRO_CLUSTER_SOURCE_ID) as
			| mapboxgl.GeoJSONSource
			| undefined

		if (!existingSource) {
			// Create the clustered source - conservative clustering, rely on anticollision
			map.current.addSource(PRO_CLUSTER_SOURCE_ID, {
				type: 'geojson',
				data: proClusterFeatures,
				cluster: true,
				clusterRadius: 50, // Tight clustering radius (only when very close)
				clusterMaxZoom: 16, // Cluster up to zoom 16, then expand
			})

			// Cluster circles - ONLY show clusters with 7+ PROs
			map.current.addLayer({
				id: PRO_CLUSTER_LAYER_ID,
				type: 'circle',
				source: PRO_CLUSTER_SOURCE_ID,
				filter: ['all', ['has', 'point_count'], ['>=', ['get', 'point_count'], 7]],
				paint: {
					'circle-color': '#1e40af',
					'circle-radius': ['step', ['get', 'point_count'], 25, 10, 30, 20, 35],
					'circle-stroke-width': 2,
					'circle-stroke-color': '#fff',
					'circle-opacity': 0.9,
				},
			})

			// Cluster count labels - ONLY for clusters with 7+ PROs
			map.current.addLayer({
				id: PRO_CLUSTER_COUNT_LAYER_ID,
				type: 'symbol',
				source: PRO_CLUSTER_SOURCE_ID,
				filter: ['all', ['has', 'point_count'], ['>=', ['get', 'point_count'], 7]],
				layout: {
					'text-field': ['get', 'point_count_abbreviated'],
					'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
					'text-size': 13,
				},
				paint: {
					'text-color': '#fff',
				},
			})

			// Small cluster indicators (2-6 PROs) - light blue dots
			map.current.addLayer({
				id: 'pro-small-clusters',
				type: 'circle',
				source: PRO_CLUSTER_SOURCE_ID,
				filter: ['all', ['has', 'point_count'], ['<', ['get', 'point_count'], 7]],
				paint: {
					'circle-color': '#60a5fa', // Light blue
					'circle-radius': 12,
					'circle-stroke-width': 2,
					'circle-stroke-color': '#fff',
					'circle-opacity': 0.9,
				},
			})

			// Small cluster count labels
			map.current.addLayer({
				id: 'pro-small-cluster-counts',
				type: 'symbol',
				source: PRO_CLUSTER_SOURCE_ID,
				filter: ['all', ['has', 'point_count'], ['<', ['get', 'point_count'], 7]],
				layout: {
					'text-field': ['get', 'point_count_abbreviated'],
					'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
					'text-size': 11,
				},
				paint: {
					'text-color': '#fff',
				},
			})

			// Unclustered PRO number labels - clean text style with anticollision
			// Shows individual PRO numbers that aren't clustered
			map.current.addLayer({
				id: PRO_UNCLUSTERED_LAYER_ID,
				type: 'symbol',
				source: PRO_CLUSTER_SOURCE_ID,
				filter: ['!', ['has', 'point_count']],
				layout: {
					'text-field': ['get', 'pro_num'],
					'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
					'text-size': 12,
					'text-offset': [0, -1.8],
					'text-anchor': 'top',
					'text-allow-overlap': false, // Anticollision enabled
					'text-optional': false,
					'text-variable-anchor': ['top', 'bottom', 'left', 'right'], // Try multiple positions
					'text-radial-offset': 1.8,
					'text-justify': 'auto',
				},
				paint: {
					'text-color': '#1e40af',
					'text-halo-color': '#ffffff',
					'text-halo-width': 2.5,
					'text-opacity': 1,
				},
			})

			// Shared cluster hover handler function
			const clusterHoverHandler = (event: mapboxgl.MapLayerMouseEvent) => {
				if (!map.current || !event.features?.length || !popup.current) return

				const feature = event.features[0]
				const clusterId = feature.properties?.cluster_id

				if (typeof clusterId !== 'number') return

				const source = map.current.getSource(PRO_CLUSTER_SOURCE_ID) as mapboxgl.GeoJSONSource

				source.getClusterLeaves(clusterId, 100, 0, (err, leaves) => {
					if (err || !leaves || !popup.current) return

					const proNumbers = leaves
						.map((leaf) => leaf.properties?.pro_num)
						.filter(Boolean)
						.sort()

					const html = `
					<div id="pro-cluster-list" style="font-size: 12px; max-height: 200px; overflow-y: auto;">
						<strong style="display: block; margin-bottom: 4px;">${proNumbers.length} PROs:</strong>
						${proNumbers.map((pro) => `<div class="pro-item" style="padding: 4px 6px; margin: 2px 0; cursor: pointer; border-radius: 3px; background: #f3f4f6; transition: background 0.2s;" data-pro="${pro}" onmouseover="this.style.background='#e5e7eb'" onmouseout="this.style.background='#f3f4f6'">${pro}</div>`).join('')}
					</div>
				`

					popup.current
						?.setLngLat((feature.geometry as GeoJSON.Point).coordinates as [number, number])
						.setHTML(html)
						.addTo(map.current as any)

					// Use event delegation on the popup container
					setTimeout(() => {
						const container = document.getElementById('pro-cluster-list')
						if (!container) return

						// Remove any existing listeners to prevent duplicates
						const newContainer = container.cloneNode(true) as HTMLElement
						container.parentNode?.replaceChild(newContainer, container)

						// Add single delegated click handler
						newContainer.addEventListener('click', (e) => {
							const target = e.target as HTMLElement
							if (target.classList.contains('pro-item')) {
								const pro = target.getAttribute('data-pro')
								const segment = segments.find((s) => s.pro_num === pro)
								if (segment && map.current) {
									const bounds = new mapboxgl.LngLatBounds()
									const [[w, s_lat], [e, n]] = segment.bounds_json
									bounds.extend([w, s_lat])
									bounds.extend([e, n])

									map.current.fitBounds(bounds as any, {
										padding: 50,
										maxZoom: 12,
										duration: 1000,
										essential: true,
									})
									popup.current?.remove()

									// Send message to parent (same as waypoint click - including trailer)
									if (_onTripSelect) {
										_onTripSelect(segment.trip_id)
									}

									sendToParent({
										type: 'tripClicked',
										trip_id: segment.trip_id,
										pro_num: segment.pro_num,
										trailer: segment.meta?.trailer,
									})
								}
							}
						})
					}, 10)
				})
			}

			// Hover interaction for large clusters (7+) - show list of PRO numbers
			registerInteractionSpec([
				{
					layerId: PRO_CLUSTER_LAYER_ID,
					events: [
						{
							type: 'mouseenter',
							handler: () => {
								if (map.current) {
									map.current.getCanvas().style.cursor = 'pointer'
								}
							},
						},
						{
							type: 'mouseleave',
							handler: () => {
								if (map.current) {
									map.current.getCanvas().style.cursor = ''
								}
								// Don't remove popup - let user click PRO numbers in the list
							},
						},
						{
							type: 'mousemove',
							handler: clusterHoverHandler,
						},
					],
				},
				{
					layerId: 'pro-small-clusters',
					events: [
						{
							type: 'mouseenter',
							handler: () => {
								if (map.current) {
									map.current.getCanvas().style.cursor = 'pointer'
								}
							},
						},
						{
							type: 'mouseleave',
							handler: () => {
								if (map.current) {
									map.current.getCanvas().style.cursor = ''
								}
								// Don't remove popup - let user click PRO numbers in the list
							},
						},
						{
							type: 'mousemove',
							handler: clusterHoverHandler,
						},
					],
				},
				{
					layerId: PRO_UNCLUSTERED_LAYER_ID,
					events: [
						{
							type: 'mouseenter',
							handler: () => {
								if (map.current) {
									map.current.getCanvas().style.cursor = 'pointer'
								}
							},
						},
						{
							type: 'mouseleave',
							handler: () => {
								if (map.current) {
									map.current.getCanvas().style.cursor = ''
								}
								popup.current?.remove()
							},
						},
						{
							type: 'click',
							handler: (event) => {
								if (!map.current || !event.features?.length) return

								const feature = event.features[0]
								const pro = feature.properties?.pro_num

								// If it's an individual PRO (not a small cluster), zoom to it
								if (pro && !feature.properties?.point_count) {
									const segment = segments.find((s) => s.pro_num === pro)

									if (segment) {
										const bounds = new mapboxgl.LngLatBounds()
										const [[w, s_lat], [e, n]] = segment.bounds_json
										bounds.extend([w, s_lat])
										bounds.extend([e, n])

										map.current.fitBounds(bounds as any, {
											padding: 50,
											maxZoom: 12,
											duration: 1000,
											essential: true,
										})

										// Send message to parent (same as waypoint click)
										if (_onTripSelect) {
											_onTripSelect(segment.trip_id)
										}

										sendToParent({
											type: 'tripClicked',
											trip_id: segment.trip_id,
											pro_num: segment.pro_num,
											trailer: segment.meta?.trailer,
										})
									}
								}
							},
						},
					],
				},
			])
		} else {
			// Update existing source with new data
			existingSource.setData(proClusterFeatures)
		}
	}, [mapLoaded, segments, rawPings])

	useEffect(() => {
		// Create stable keys to detect actual changes vs reference updates
		const tripIdsKey = segments
			.map((s) => s.trip_id)
			.sort()
			.join(',')
		const rawPingsKeysKey = Object.keys(rawPings).sort().join(',')

		const tripIdsChanged = tripIdsKey !== previousTripIdsRef.current
		const rawPingsChanged = rawPingsKeysKey !== previousRawPingsKeysRef.current

		if (!map.current || !mapLoaded || !customIconLoaded) {
			return
		}

		// Skip if neither trip IDs nor rawPings have changed (just a reference update)
		if (!tripIdsChanged && !rawPingsChanged) {
			return
		}
		previousTripIdsRef.current = tripIdsKey
		previousRawPingsKeysRef.current = rawPingsKeysKey

		// Get current trip IDs on the map
		const currentTripIds = new Set(
			Object.keys(map.current.getStyle().sources || {})
				.filter((id) => id.startsWith('trip-'))
				.map((id) => id.replace('trip-', '').replace('-marker', ''))
		)

		// Get new trip IDs from segments
		const newTripIds = new Set(segments.map((s) => s.trip_id))

		// Remove all existing trip layers (we'll recreate them with current icon mode)
		currentTripIds.forEach((tripId) => {
			const sourceId = `trip-${tripId}`

			// Always remove all layers for this trip (to handle icon mode switches)
			// Need to check in reverse order they were added to avoid dependency issues
			const layersToRemove = [
				`${sourceId}-pill`,
				`${sourceId}-stops-labels`,
				`${sourceId}-stops-dots`,
				`${sourceId}-label-hover`,
				`${sourceId}-label`,
				`${sourceId}-arrow`,
				`${sourceId}-point`,
				`${sourceId}-line-hover`, // Hover layer (monochrome mode only)
				`${sourceId}-line`,
				`${sourceId}-breadcrumbs-hover`, // Remove breadcrumbs hover layer
				`${sourceId}-breadcrumbs`, // Remove breadcrumbs layer
				`${sourceId}-stopped-dot`, // Marker stopped dot layer
				`${sourceId}-stopped`, // Marker stopped layer
			]
			layersToRemove.forEach((layerId) => {
				if (map.current!.getLayer(layerId)) {
					map.current!.removeLayer(layerId)
				}
			})

			// Only remove sources if trip is no longer in the list
			if (!newTripIds.has(tripId)) {
				if (map.current!.getSource(sourceId)) {
					map.current!.removeSource(sourceId)
				}
				if (map.current!.getSource(`${sourceId}-marker`)) {
					map.current!.removeSource(`${sourceId}-marker`)
				}
				if (map.current!.getSource(`${sourceId}-stops`)) {
					map.current!.removeSource(`${sourceId}-stops`)
				}
				if (map.current!.getSource(`${sourceId}-pings`)) {
					map.current!.removeSource(`${sourceId}-pings`)
				}
			}
		})

		// If no segments to show, just fit bounds to a default view
		if (!segments.length) {
			return
		}

		// DEPRECATED: Track segments that need hover layers (disabled - breadcrumbs only)
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const segmentsNeedingHoverLayers: Array<{
			sourceId: string
			isSinglePoint: boolean
			segment: TripSegment
		}> = []

		// Add sources and layers for each trip
		segments.forEach((segment) => {
			const sourceId = `trip-${segment.trip_id}`
			const pings = rawPings[segment.trip_id] || []
			const isSinglePoint = pings.length <= 1
			const { lastCoord, secondLastCoord } = resolveMarkerCoordinates(pings, segment)
			const tripInteractions: InteractionSpec[] = []

			// Clean up layers then sources to avoid “source in use” errors
			try {
				const layers = map.current!.getStyle().layers || []
				// Remove any layer that belongs to this trip (prefix match)
				for (let i = layers.length - 1; i >= 0; i -= 1) {
					const layerId = layers[i].id
					if (layerId.startsWith(`${sourceId}-`)) {
						if (map.current!.getLayer(layerId)) {
							map.current!.removeLayer(layerId)
						}
					}
				}

				// Remove sources for this trip (prefix match)
				const sources = map.current!.getStyle().sources || {}
				Object.keys(sources).forEach((sourceKey) => {
					if (sourceKey === sourceId || sourceKey.startsWith(`${sourceId}-`)) {
						if (map.current!.getSource(sourceKey)) {
							map.current!.removeSource(sourceKey)
						}
					}
				})
			} catch {
				// Ignore cleanup errors
			}

			const color = getSegmentColor(segment, colorMode)
			const colorId = color.replace(/[(),\s%#]/g, '')

			// Badge color for monochrome mode (lighter default, darker on hover)
			const badgeColorDefault = colorMode === 'monochrome' ? '#93c5fd' : color
			const badgeColorDefaultId = badgeColorDefault.replace(/[(),\s%#]/g, '')

			// Handle single point trips (no route yet)
			if (isSinglePoint) {
				const pointCoord = lastCoord

				// Add point source
				map.current!.addSource(`${sourceId}-marker`, {
					type: 'geojson',
					data: {
						type: 'Feature',
						id: 0, // Required for setFeatureState to work
						geometry: {
							type: 'Point',
							coordinates: pointCoord,
						},
						properties: {
							trip_id: segment.trip_id,
							pro_num: segment.pro_num,
							trailer: segment.meta?.trailer,
							color: color,
							color_id: colorId,
							color_badge: badgeColorDefaultId,
							color_badge_default: badgeColorDefault, // Raw badge color for default state
							is_single_point: true,
						},
					},
				})

				// Render as a dot (no arrow/heading indicator)
				map.current!.addLayer({
					id: `${sourceId}-point`,
					type: 'circle',
					source: `${sourceId}-marker`,
					minzoom: DETAIL_VIEW_ZOOM - 0.5,
					paint: {
						'circle-radius': 12,
						'circle-color': [
							'case',
							['boolean', ['feature-state', 'selected'], false],
							'#2563eb', // Selected: Bright blue (same as hover)
							['boolean', ['feature-state', 'hover'], false],
							'#2563eb', // Hover: Bright blue
							[
								'all',
								['boolean', ['feature-state', 'has_selection'], false],
								['!', ['boolean', ['feature-state', 'selected'], false]],
							],
							'#cbd5e1', // When another trip is selected: Very light gray
							color,
						],
						'circle-stroke-width': 3,
						'circle-stroke-color': [
							'case',
							['boolean', ['feature-state', 'selected'], false],
							'#1e3a8a', // Selected: Dark blue stroke
							['boolean', ['feature-state', 'hover'], false],
							'#1e3a8a', // Hover: Dark blue stroke
							[
								'all',
								['boolean', ['feature-state', 'has_selection'], false],
								['!', ['boolean', ['feature-state', 'selected'], false]],
							],
							'#e2e8f0', // When another trip is selected: Very light gray
							'#fff',
						],
						'circle-opacity': [
							'case',
							['boolean', ['feature-state', 'selected'], false],
							0.95, // Selected: High opacity
							['boolean', ['feature-state', 'hover'], false],
							0.9, // Hover: High opacity
							[
								'all',
								['boolean', ['feature-state', 'has_selection'], false],
								['!', ['boolean', ['feature-state', 'selected'], false]],
							],
							0.15, // When another trip is selected: Very light
							0.7, // Default
						],
					},
				})
			} else {
				// Multi-point trip with route
				// Skip if snapped_geojson is missing or invalid
				if (!segment.snapped_geojson?.coordinates) {
					console.warn(`Skipping segment ${segment.trip_id}: missing snapped_geojson`)
					return
				}

				// Wrap LineString in a Feature for Mapbox
				const geojsonFeature: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString> = {
					type: 'Feature',
					id: 0, // Required for setFeatureState to work
					geometry: segment.snapped_geojson as GeoJSON.LineString | GeoJSON.MultiLineString,
					properties: {
						trip_id: segment.trip_id,
						pro_num: segment.pro_num,
					},
				}

				map.current!.addSource(sourceId, {
					type: 'geojson',
					data: geojsonFeature,
				})

				// DEPRECATED: Line routes disabled - breadcrumbs only
				// Check if this route has been snapped
				// const pings = rawPings[segment.trip_id] || []
				// const hasSnappedRoute = isRouteSnapped(segment, pings)

				// Line layer with dotted pattern (only show if route has been snapped)
				// DISABLED: We're only showing breadcrumbs now
				/*
				const lineColorBase = colorMode === 'monochrome' ? '#95b3f0' : color

				if (hasSnappedRoute) {
					map.current!.addLayer({
						id: `${sourceId}-line`,
						type: 'line',
						source: sourceId,
						layout: {
							'line-cap': 'round',
							'line-join': 'round',
						},
						paint: {
							'line-color': lineColorBase,
							'line-width': 3,
							'line-opacity': colorMode === 'monochrome' ? 1 : 0.7,
							'line-dasharray': [1, 2.5],
						},
					})

					if (colorMode === 'monochrome' && !isSinglePoint) {
						segmentsNeedingHoverLayers.push({ sourceId, isSinglePoint, segment })
					}

					map.current!.on('mouseenter', `${sourceId}-line`, () => {
						if (map.current) {
							map.current.getCanvas().style.cursor = 'pointer'
							if (colorMode === 'monochrome') {
								map.current.setFeatureState({ source: sourceId, id: 0 }, { hover: true })
							}
						}
					})

					map.current!.on('mouseleave', `${sourceId}-line`, () => {
						if (map.current) {
							map.current.getCanvas().style.cursor = ''
							if (colorMode === 'monochrome') {
								map.current.setFeatureState({ source: sourceId, id: 0 }, { hover: false })
							}
						}
					})
				}
				*/

				// Always render raw pings as breadcrumbs (solid, light color by default)
				const pings = rawPings[segment.trip_id] || []
				if (pings && pings.length > 0) {
					const breadcrumbFeature: GeoJSON.Feature<GeoJSON.MultiPoint> = {
						type: 'Feature',
						id: 0,
						geometry: {
							type: 'MultiPoint',
							coordinates: pings.map((ping) => [ping.lng, ping.lat]),
						},
						properties: {
							trip_id: segment.trip_id,
						},
					}

					map.current!.addSource(`${sourceId}-pings`, {
						type: 'geojson',
						data: breadcrumbFeature,
					})

					// Breadcrumbs: semi-transparent light blue with solid dark blue border
					// When hovered, visible at all zoom levels; otherwise only at detail zoom
					map.current!.addLayer({
						id: `${sourceId}-breadcrumbs`,
						type: 'circle',
						source: `${sourceId}-pings`,
						minzoom: DETAIL_VIEW_ZOOM - 0.5, // Visible at zoom 6.5+
						paint: {
							'circle-radius': 4,
							'circle-color': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								'#004cfe', // Selected: Bright blue
								['boolean', ['feature-state', 'hover'], false],
								'#004cfe', // Hover: Bright blue
								[
									'all',
									['boolean', ['feature-state', 'has_selection'], false],
									['!', ['boolean', ['feature-state', 'selected'], false]],
								],
								'#cbd5e1', // When another trip is selected: Very light gray
								'#93c5fd', // Default: Light blue
							],
							'circle-opacity': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								1, // Selected: Solid
								['boolean', ['feature-state', 'hover'], false],
								1, // Hover: Solid
								[
									'all',
									['boolean', ['feature-state', 'has_selection'], false],
									['!', ['boolean', ['feature-state', 'selected'], false]],
								],
								0.15, // When another trip is selected: Very light
								0.3, // Default: Transparent
							],
							'circle-stroke-width': 1.5,
							'circle-stroke-color': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								'#1e40af', // Selected: Dark blue
								['boolean', ['feature-state', 'hover'], false],
								'#1e40af', // Hover: Dark blue
								[
									'all',
									['boolean', ['feature-state', 'has_selection'], false],
									['!', ['boolean', ['feature-state', 'selected'], false]],
								],
								'#e2e8f0', // When another trip is selected: Very light gray
								'#1e40af', // Default: Dark blue
							],
							'circle-stroke-opacity': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								1, // Selected: Solid stroke
								['boolean', ['feature-state', 'hover'], false],
								1, // Hover: Solid stroke
								[
									'all',
									['boolean', ['feature-state', 'has_selection'], false],
									['!', ['boolean', ['feature-state', 'selected'], false]],
								],
								0.2, // When another trip is selected: Very light
								0.8, // Default
							],
						},
					})

					// Breadcrumbs hover layer - shows at ALL zoom levels (no minzoom)
					map.current!.addLayer({
						id: `${sourceId}-breadcrumbs-hover`,
						type: 'circle',
						source: `${sourceId}-pings`,
						paint: {
							'circle-radius': 4,
							'circle-color': '#004cfe', // Bright blue
							'circle-opacity': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								1, // Selected: Visible
								['boolean', ['feature-state', 'hover'], false],
								1, // Hover: Visible
								0, // Default: Hidden
							],
							'circle-stroke-width': 1.5,
							'circle-stroke-color': '#1e40af', // Solid dark blue border
							'circle-stroke-opacity': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								1, // Selected: Visible
								['boolean', ['feature-state', 'hover'], false],
								1, // Hover: Visible
								0, // Default: Hidden
							],
						},
					})
				}

				// Check for co-located stops
				let colocatedStopIndex = -1
				let colocatedStopData: { sequence: string; fill: string; border: string } | null = null

				if (!isSinglePoint && segment.meta?.stops) {
					colocatedStopIndex = segment.meta.stops.findIndex(
						(stop) => stop.coordinates && areCoordsEqual(stop.coordinates, lastCoord)
					)

					if (colocatedStopIndex > -1) {
						const stop = segment.meta.stops[colocatedStopIndex]
						const isDelivered =
							stop.delivered || stop.shipment_status === 'Delivered' || segment.order_status === 'D'

						// Stop colors (matching the logic in stops rendering)
						const stopFill = isDelivered ? '#86efac' : '#93c5fd'
						const stopBorder = '#1e40af'

						colocatedStopData = {
							sequence: String(stop.sequence),
							fill: stopFill.startsWith('hsl') ? hslToHex(stopFill) : stopFill,
							border: stopBorder.startsWith('hsl') ? hslToHex(stopBorder) : stopBorder,
						}
					}
				}

				// Calculate bearing for icon direction
				const computedDistance = distanceMetersBetween(secondLastCoord, lastCoord)
				const distanceMeters = Number.isFinite(computedDistance)
					? computedDistance
					: Number.POSITIVE_INFINITY
				const isStopped = distanceMeters < 50

				let bearing = 0
				try {
					bearing = calculateBearing(secondLastCoord, lastCoord)
				} catch {
					bearing = 0
				}

				// Truck SVG points RIGHT by default (210px wide at 0.5 scale = 105px)
				// Rotate so front of truck aligns with bearing direction
				const truckRotation = bearing - 90

				// Add a separate point source for the marker
				map.current!.addSource(`${sourceId}-marker`, {
					type: 'geojson',
					data: {
						type: 'Feature',
						id: 0, // Required for setFeatureState to work
						geometry: {
							type: 'Point',
							coordinates: lastCoord,
						},
						properties: {
							trip_id: segment.trip_id,
							pro_num: segment.pro_num,
							trailer: segment.meta?.trailer,
							bearing: bearing,
							truck_rotation: truckRotation,
							is_stopped: isStopped,
							color: color,
							color_id: colorId,
							color_badge: badgeColorDefaultId,
							color_badge_default: badgeColorDefault, // Raw badge color for default state
							is_single_point: false,
							// Co-location properties
							colocated_sequence: colocatedStopData?.sequence,
							colocated_fill: colocatedStopData?.fill.replace(/[#]/g, ''),
							colocated_border: colocatedStopData?.border.replace(/[#]/g, ''),
						},
					},
				})

				// Heading marker is always 100% opaque (even in monochrome mode)
				const markerOpacity = 1

				// Check if there's a colocated stop - if so, show pill to the right
				const hasColocatedStop = colocatedStopIndex > -1

				if (hasColocatedStop) {
					// Show pill with stop info (always horizontal, no rotation)
					// Waypoint icon is separate and rotates based on bearing, positioned at left side of pill
					const iconType = iconMode === 'truck' ? 'truck' : 'arrow'
					// Offset pill right so the circle inside the pill aligns with the waypoint arrow
					// Shift pill further right to reduce overlap with waypoint circle
					const pillOffsetX = 12.5
					map.current!.addLayer({
						id: `${sourceId}-pill`,
						type: 'symbol',
						source: `${sourceId}-marker`,
						layout: {
							'icon-image': [
								'concat',
								'combo-pill-',
								iconType,
								'-',
								colorId,
								'-',
								['get', 'colocated_sequence'],
								'-',
								['get', 'colocated_fill'],
								'-',
								['get', 'colocated_border'],
							],
							'icon-size': 1,
							// Pill is always horizontal - no rotation
							'icon-rotation-alignment': 'map',
							'icon-allow-overlap': true,
							'icon-ignore-placement': true,
							// Offset pill right so waypoint appears on left side
							'icon-offset': [pillOffsetX, 0],
						},
						paint: {
							'icon-opacity': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								markerOpacity, // Selected: Full opacity
								['boolean', ['feature-state', 'hover'], false],
								markerOpacity, // Hover: Full opacity
								[
									'all',
									['boolean', ['feature-state', 'has_selection'], false],
									['!', ['boolean', ['feature-state', 'selected'], false]],
								],
								0.15, // When another trip is selected: Very light
								markerOpacity, // Default: Full opacity
							],
						},
					})

					tripInteractions.push({
						layerId: `${sourceId}-pill`,
						events: [
							{
								type: 'mouseenter',
								handler: () => {
									if (map.current) {
										map.current.getCanvas().style.cursor = 'pointer'
										bringTripToTop()
										setRouteHover(true)
									}
								},
							},
							{
								type: 'mouseleave',
								handler: () => {
									if (map.current) {
										map.current.getCanvas().style.cursor = ''
										removeTripFromTop()
										setRouteHover(false)
									}
								},
							},
							{
								type: 'click',
								handler: handleMarkerClick,
							},
						],
					})
				}

				if (iconMode === 'truck') {
					// Custom truck SVG icon (rotates based on bearing)
					// No separate arrow layer in truck mode - the truck icon shows direction
					map.current!.addLayer({
						id: `${sourceId}-point`,
						type: 'symbol',
						source: `${sourceId}-marker`,
						layout: {
							'icon-image': ['concat', 'truck-icon-', colorId, '-flip0'],
							'icon-size': 0.5, // 210x136 SVG at 0.5 scale = 105x68px
							'icon-rotate': ['get', 'truck_rotation'],
							'icon-rotation-alignment': 'map',
							'icon-allow-overlap': true,
							'icon-ignore-placement': true,
							// Offset: [-26, 34] = shift back 25%, down 50% of height
							// This positions the front of the truck ahead of the route point, tires on road
							// When colocated, pill is offset right, so truck stays centered
							'icon-offset': hasColocatedStop ? [-26, 0] : [-26, 34],
						},
						paint: {
							'icon-opacity': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								markerOpacity, // Selected: Full opacity
								['boolean', ['feature-state', 'hover'], false],
								markerOpacity, // Hover: Full opacity
								[
									'all',
									['boolean', ['feature-state', 'has_selection'], false],
									['!', ['boolean', ['feature-state', 'selected'], false]],
								],
								0.15, // When another trip is selected: Very light
								markerOpacity, // Default: Full opacity
							],
						},
						filter: ['==', ['get', 'is_stopped'], false],
					})
				} else {
					// Arrow mode - always use the same waypoint style; pill is additive for colocation
					const arrowSize = hasColocatedStop ? 0.6 : 0.6
					const circleRadius = hasColocatedStop ? 10 : 10

					// Circle background - semi-transparent white with solid dark blue border
					map.current!.addLayer({
						id: `${sourceId}-point`,
						type: 'circle',
						source: `${sourceId}-marker`,
						paint: {
							'circle-radius': circleRadius, // Slightly smaller when pill is present
							'circle-color': '#ffffff',
							'circle-opacity': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								0.9, // Selected
								['boolean', ['feature-state', 'hover'], false],
								0.7, // Hover
								[
									'all',
									['boolean', ['feature-state', 'has_selection'], false],
									['!', ['boolean', ['feature-state', 'selected'], false]],
								],
								0.15, // When another trip is selected: Very light
								0.5, // Default
							],
							'circle-stroke-width': hasColocatedStop ? 2 : 2,
							'circle-stroke-color': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								'#1e40af', // Selected: Dark blue
								['boolean', ['feature-state', 'hover'], false],
								'#1e40af', // Hover: Dark blue
								[
									'all',
									['boolean', ['feature-state', 'has_selection'], false],
									['!', ['boolean', ['feature-state', 'selected'], false]],
								],
								'#e2e8f0', // When another trip is selected: Very light gray
								'#1e40af', // Default: Dark blue
							],
							'circle-stroke-opacity': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								1, // Selected: Solid
								['boolean', ['feature-state', 'hover'], false],
								1, // Hover: Solid
								[
									'all',
									['boolean', ['feature-state', 'has_selection'], false],
									['!', ['boolean', ['feature-state', 'selected'], false]],
								],
								0.2, // When another trip is selected: Very light
								1, // Default: Solid
							],
						},
						filter: ['==', ['get', 'is_stopped'], false],
					})

					// Arrow icon on top - solid dark blue arrow
					map.current!.addLayer({
						id: `${sourceId}-arrow`,
						type: 'symbol',
						source: `${sourceId}-marker`,
						layout: {
							'icon-image': 'arrow-#1e40af', // Dark blue arrow
							'icon-size': arrowSize,
							'icon-rotate': ['get', 'bearing'],
							'icon-rotation-alignment': 'map',
							'icon-allow-overlap': true,
							'icon-ignore-placement': true,
							// Arrow is centered on waypoint
							'icon-offset': [0, 0],
						},
						paint: {
							'icon-opacity': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								markerOpacity, // Selected: Full opacity
								['boolean', ['feature-state', 'hover'], false],
								markerOpacity, // Hover: Full opacity
								[
									'all',
									['boolean', ['feature-state', 'has_selection'], false],
									['!', ['boolean', ['feature-state', 'selected'], false]],
								],
								0.15, // When another trip is selected: Very light
								markerOpacity, // Default: Full opacity
							],
						},
						filter: ['==', ['get', 'is_stopped'], false],
					})
				}

				// Stopped indicator: neutral dot when last two coords are effectively the same
				map.current!.addLayer({
					id: `${sourceId}-stopped`,
					type: 'circle',
					source: `${sourceId}-marker`,
					filter: ['==', ['get', 'is_stopped'], true],
					paint: {
						// Match the arrow-circle background style
						'circle-radius': 10,
						'circle-color': '#ffffff',
						'circle-opacity': [
							'case',
							['boolean', ['feature-state', 'selected'], false],
							1,
							['boolean', ['feature-state', 'hover'], false],
							0.85,
							[
								'all',
								['boolean', ['feature-state', 'has_selection'], false],
								['!', ['boolean', ['feature-state', 'selected'], false]],
							],
							0.2,
							0.9,
						],
						'circle-stroke-width': 2,
						'circle-stroke-color': '#1e40af',
						'circle-stroke-opacity': [
							'case',
							['boolean', ['feature-state', 'selected'], false],
							1,
							['boolean', ['feature-state', 'hover'], false],
							1,
							[
								'all',
								['boolean', ['feature-state', 'has_selection'], false],
								['!', ['boolean', ['feature-state', 'selected'], false]],
							],
							0.2,
							0.9,
						],
					},
				})

				// Inner dot for stopped state (keep above the pill and outer circle)
				map.current!.addLayer({
					id: `${sourceId}-stopped-dot`,
					type: 'circle',
					source: `${sourceId}-marker`,
					filter: ['==', ['get', 'is_stopped'], true],
					paint: {
						'circle-radius': 3,
						'circle-color': '#1e40af',
						'circle-opacity': [
							'case',
							['boolean', ['feature-state', 'selected'], false],
							1,
							['boolean', ['feature-state', 'hover'], false],
							0.9,
							[
								'all',
								['boolean', ['feature-state', 'has_selection'], false],
								['!', ['boolean', ['feature-state', 'selected'], false]],
							],
							0.2,
							0.9,
						],
						'circle-stroke-width': 1,
						'circle-stroke-color': '#ffffff',
						'circle-stroke-opacity': [
							'case',
							['boolean', ['feature-state', 'selected'], false],
							1,
							['boolean', ['feature-state', 'hover'], false],
							1,
							[
								'all',
								['boolean', ['feature-state', 'has_selection'], false],
								['!', ['boolean', ['feature-state', 'selected'], false]],
							],
							0.2,
							0.9,
						],
					},
				})

				tripInteractions.push({
					layerId: `${sourceId}-stopped`,
					events: [
						{
							type: 'mouseenter',
							handler: () => {
								if (map.current) {
									map.current.getCanvas().style.cursor = 'pointer'
									bringTripToTop()
									setRouteHover(true)
								}
							},
						},
						{
							type: 'mouseleave',
							handler: () => {
								if (map.current) {
									map.current.getCanvas().style.cursor = ''
									removeTripFromTop()
									setRouteHover(false)
								}
							},
						},
						{
							type: 'click',
							handler: handleMarkerClick,
						},
					],
				})
			}

			// PRO number badge label - HIDDEN (now using clustered labels at top level)
			// Keeping layer structure for future use, but making it invisible
			const textColor =
				colorMode === 'monochrome'
					? [
							'case',
							['boolean', ['feature-state', 'hover'], false],
							'#ffffff', // Hovered: white text
							'#1e3a8a', // Default: dark blue text for contrast
						]
					: getContrastColor(color)

			const badgeIconImage =
				colorMode === 'monochrome'
					? ['concat', 'badge-', ['get', 'color_badge_default']] // Default (light) badge image
					: ['concat', 'badge-', ['get', 'color']]

			map.current!.addLayer({
				id: `${sourceId}-label`,
				type: 'symbol',
				source: `${sourceId}-marker`,
				minzoom: DETAIL_VIEW_ZOOM - 0.3,
				layout: {
					'text-field': ['get', 'pro_num'],
					'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
					'text-size': 12,
					'text-radial-offset': 2.3,
					'text-variable-anchor':
						colorMode === 'monochrome' ? ['right'] : ['right', 'left', 'top', 'bottom'],
					'text-justify': 'auto',
					'text-allow-overlap': colorMode === 'monochrome',
					'text-optional': colorMode !== 'monochrome',
					'icon-image': badgeIconImage as any,
					'icon-text-fit': 'both',
					'icon-text-fit-padding': [2, 4, 2, 4],
					'icon-allow-overlap': colorMode === 'monochrome',
					'icon-optional': true,
					visibility: 'none', // Hidden - using clustered labels instead
				},
				paint: {
					'text-color': textColor as any,
					'text-opacity': 0,
					'icon-opacity': 0,
				},
			})

			// In monochrome, add a separate hover label layer - HIDDEN (using clustered labels)
			if (colorMode === 'monochrome') {
				map.current!.addLayer(
					{
						id: `${sourceId}-label-hover`,
						type: 'symbol',
						source: `${sourceId}-marker`,
						minzoom: DETAIL_VIEW_ZOOM - 0.3,
						layout: {
							'text-field': ['get', 'pro_num'],
							'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
							'text-size': 12,
							'text-radial-offset': 2.3,
							'text-variable-anchor': ['right'],
							'text-justify': 'auto',
							'text-allow-overlap': true,
							'text-optional': false,
							'icon-image': ['concat', 'badge-', ['get', 'color']],
							'icon-text-fit': 'both',
							'icon-text-fit-padding': [2, 4, 2, 4],
							'icon-allow-overlap': true,
							'icon-optional': true,
							visibility: 'none', // Hidden - using clustered labels instead
						},
						paint: {
							'text-opacity': 0,
							'icon-opacity': 0,
						},
					},
					`${sourceId}-label`
				)

				tripInteractions.push({
					layerId: `${sourceId}-label-hover`,
					events: [
						{
							type: 'mouseenter',
							handler: () => {
								if (map.current) {
									map.current.getCanvas().style.cursor = 'pointer'
									setRouteHover(true)
								}
							},
						},
						{
							type: 'mouseleave',
							handler: () => {
								if (map.current) {
									map.current.getCanvas().style.cursor = ''
									setRouteHover(false)
								}
							},
						},
					],
				})
			}

			// Helper to bring trip to top and set hover state
			const bringTripToTop = () => {
				if (!map.current) return

				hoveredTripIdRef.current = segment.trip_id

				// Move all layers for this trip to the top (waypoints must be last/highest)
				const layersToMove = [
					`${sourceId}-breadcrumbs`,
					`${sourceId}-breadcrumbs-hover`,
					`${sourceId}-stops-dots`,
					`${sourceId}-stops-labels`,
					`${sourceId}-pill`,
					`${sourceId}-point`,
					`${sourceId}-arrow`,
					`${sourceId}-label`,
					`${sourceId}-label-hover`,
				]

				layersToMove.forEach((layerId) => {
					if (map.current!.getLayer(layerId)) {
						try {
							map.current!.moveLayer(layerId)
						} catch {
							// Layer might not exist yet, ignore
						}
					}
				})
			}

			const removeTripFromTop = () => {
				hoveredTripIdRef.current = null
			}

			// Helper to set hover state on all related elements
			const setRouteHover = (isHovered: boolean) => {
				if (!map.current) return

				const setState = (source: string, id: number) => {
					if (!map.current?.getSource(source)) {
						return
					}

					try {
						map.current.setFeatureState({ source, id }, { hover: isHovered })
					} catch {
						// Feature may not exist yet; ignore
					}
				}

				setState(`${sourceId}-marker`, 0)

				if (!isSinglePoint && segment.meta?.stops) {
					const stopsWithCoords = segment.meta.stops.filter((stop) => stop.coordinates)
					stopsWithCoords.forEach((_, idx) => {
						setState(`${sourceId}-stops`, idx)
					})
				}

				if (map.current.getSource(`${sourceId}-pings`)) {
					setState(`${sourceId}-pings`, 0)
				}
			}

			function handleMarkerClick(event: mapboxgl.MapLayerMouseEvent) {
				if (!event.features || !event.features.length) {
					return
				}

				const feature = event.features[0]
				const properties = feature.properties as Record<string, any> | undefined

				if (!properties?.trip_id || !properties?.pro_num) {
					return
				}

				const now = Date.now()
				const lastClicked = lastClickedProRef.current

				if (
					lastClicked &&
					lastClicked.pro_num === properties.pro_num &&
					now - lastClicked.timestamp < 50
				) {
					return
				}

				lastClickedProRef.current = { pro_num: properties.pro_num, timestamp: now }

				// Zoom to full route bounds instead of just the waypoint
				if (map.current) {
					// Try finding by trip_id first
					let segment = segments.find((s) => s.trip_id === properties.trip_id)

					// If not found, try finding by pro_num (if available in properties)
					if (!segment && properties.pro_num) {
						segment = segments.find((s) => s.pro_num === properties.pro_num)
					}

					if (segment) {
						const bounds = new mapboxgl.LngLatBounds()
						const [[w, s_lat], [e, n]] = segment.bounds_json
						bounds.extend([w, s_lat])
						bounds.extend([e, n])

						map.current.fitBounds(bounds as any, {
							padding: 50,
							maxZoom: 12,
							duration: 1000,
							essential: true,
						})
					}
				}

				// Notify parent component about selection
				if (_onTripSelect) {
					_onTripSelect(properties.trip_id)
				}

				sendToParent({
					type: 'tripClicked',
					trip_id: properties.trip_id,
					pro_num: properties.pro_num,
					trailer: properties.trailer,
				})
			}

			tripInteractions.push({
				layerId: `${sourceId}-point`,
				events: [
					{
						type: 'mouseenter',
						handler: () => {
							if (map.current) {
								map.current.getCanvas().style.cursor = 'pointer'
								bringTripToTop()
								setRouteHover(true)
							}
						},
					},
					{
						type: 'mouseleave',
						handler: () => {
							if (map.current) {
								map.current.getCanvas().style.cursor = ''
								removeTripFromTop()
								setRouteHover(false)
							}
						},
					},
					{
						type: 'click',
						handler: handleMarkerClick,
					},
				],
			})

			tripInteractions.push({
				layerId: `${sourceId}-label`,
				events: [
					{
						type: 'mouseenter',
						handler: () => {
							if (map.current) {
								map.current.getCanvas().style.cursor = 'pointer'
								bringTripToTop()
								setRouteHover(true)
							}
						},
					},
					{
						type: 'mouseleave',
						handler: () => {
							if (map.current) {
								map.current.getCanvas().style.cursor = ''
								removeTripFromTop()
								setRouteHover(false)
							}
						},
					},
					{
						type: 'click',
						handler: handleMarkerClick,
					},
				],
			})

			if (pings && pings.length > 0) {
				// Shared hover handlers for both breadcrumb layers
				const breadcrumbMouseEnter = () => {
					if (map.current) {
						map.current.getCanvas().style.cursor = 'pointer'
						bringTripToTop()
						setRouteHover(true)

						// Best Practice: Use feature-state for hover (Step 7 from tutorial)
						// Clear previous hover state
						if (hoveredFeatureRef.current) {
							map.current.setFeatureState(hoveredFeatureRef.current, { hover: false })
						}
						// Set new hover state
						map.current.setFeatureState({ source: `${sourceId}-pings`, id: 0 }, { hover: true })
						hoveredFeatureRef.current = { source: `${sourceId}-pings`, id: 0 }
					}
				}

				const breadcrumbMouseLeave = () => {
					if (map.current) {
						map.current.getCanvas().style.cursor = ''
						removeTripFromTop()
						setRouteHover(false)

						// Best Practice: Reset feature-state on mouseleave (Step 9 from tutorial)
						if (hoveredFeatureRef.current) {
							map.current.setFeatureState(hoveredFeatureRef.current, { hover: false })
							hoveredFeatureRef.current = null
						}
					}
				}

				// Register interactions for both regular and hover breadcrumb layers
				tripInteractions.push(
					{
						layerId: `${sourceId}-breadcrumbs`,
						events: [
							{
								type: 'mouseenter',
								handler: breadcrumbMouseEnter,
							},
							{
								type: 'mouseleave',
								handler: breadcrumbMouseLeave,
							},
						],
					},
					{
						layerId: `${sourceId}-breadcrumbs-hover`,
						events: [
							{
								type: 'mouseenter',
								handler: breadcrumbMouseEnter,
							},
							{
								type: 'mouseleave',
								handler: breadcrumbMouseLeave,
							},
						],
					}
				)
			}

			// Add stop markers if available (only for multi-point trips)
			if (!isSinglePoint && segment.meta?.stops && segment.meta.stops.length > 0) {
				const stopsSourceId = `${sourceId}-stops`

				// Remove existing stops source if any
				if (map.current!.getSource(stopsSourceId)) {
					map.current!.removeSource(stopsSourceId)
				}

				// Filter stops that have coordinates (keep co-located; we’ll hide them at low zoom to avoid overlap)
				const stopsWithCoords = segment.meta.stops.filter((stop) => stop.coordinates)

				if (stopsWithCoords.length > 0) {
					// Create GeoJSON features for stops
					const stopFeatures: GeoJSON.FeatureCollection<GeoJSON.Point> = {
						type: 'FeatureCollection',
						features: stopsWithCoords.map((stop, idx) => ({
							type: 'Feature',
							id: idx, // Unique ID for each stop (required for setFeatureState)
							geometry: {
								type: 'Point',
								coordinates: stop.coordinates!,
							},
							properties: {
								consignee_id: stop.consignee_id,
								consignee_name: stop.consignee_name || 'Unknown',
								sequence: stop.sequence,
								stop_index: idx,
								trip_id: segment.trip_id,
								pro_num: segment.pro_num,
								delivered: stop.delivered || false,
								shipment_status: stop.shipment_status || 'Unknown',
								colocated_with_truck: areCoordsEqual(stop.coordinates!, lastCoord),
							},
						})),
					}

					// Add stops source
					map.current!.addSource(stopsSourceId, {
						type: 'geojson',
						data: stopFeatures,
					})

					// Add stop markers (rounded squares with semi-transparent fill and solid dark blue border)
					// Delivered stops: green, Pending stops: light blue

					// Determine stop colors (default state) - semi-transparent with dark blue border
					let stopFillColorDefault: string
					let stopFillColorDelivered: string

					if (colorMode === 'monochrome') {
						stopFillColorDefault = '#93c5fd' // Light blue for pending (default)
						stopFillColorDelivered = '#86efac' // Light green for delivered (default)
					} else {
						stopFillColorDefault = '#93c5fd' // Light blue for all pending
						stopFillColorDelivered = '#86efac' // Light green for delivered
					}

					const stopBorderColor = '#1e40af' // Dark blue border

					// Convert colors to hex for icon IDs (HSL colors need conversion)
					const fillColorHex = stopFillColorDefault.startsWith('hsl')
						? hslToHex(stopFillColorDefault)
						: stopFillColorDefault
					const deliveredColorHex = stopFillColorDelivered.startsWith('hsl')
						? hslToHex(stopFillColorDelivered)
						: stopFillColorDelivered
					const borderColorHex = stopBorderColor.startsWith('hsl')
						? hslToHex(stopBorderColor)
						: stopBorderColor

					// Generate icon IDs for different color combinations (using hex)
					const iconIdDefault = `stop-square-${fillColorHex.replace(/[(),\s%#]/g, '')}-${borderColorHex.replace(/[(),\s%#]/g, '')}`
					const iconIdDelivered = `stop-square-${deliveredColorHex.replace(/[(),\s%#]/g, '')}-${borderColorHex.replace(/[(),\s%#]/g, '')}`

					// Pre-generate icons
					if (map.current) {
						if (!map.current.hasImage(iconIdDefault)) {
							map.current.fire('styleimagemissing', { id: iconIdDefault })
						}
						if (!map.current.hasImage(iconIdDelivered) && iconIdDelivered !== iconIdDefault) {
							map.current.fire('styleimagemissing', { id: iconIdDelivered })
						}
					}

					map.current!.addLayer({
						id: `${stopsSourceId}-dots`,
						type: 'symbol',
						source: stopsSourceId,
						minzoom: DETAIL_VIEW_ZOOM - 0.3,
						layout: {
							'icon-image': ['case', ['get', 'delivered'], iconIdDelivered, iconIdDefault] as any,
							'icon-size': 1,
							'icon-allow-overlap': true,
							'icon-ignore-placement': true,
						},
						paint: {
							'icon-opacity': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								0.9, // Selected
								['boolean', ['feature-state', 'hover'], false],
								0.9, // Hover
								[
									'all',
									['boolean', ['feature-state', 'has_selection'], false],
									['!', ['boolean', ['feature-state', 'selected'], false]],
								],
								0.15, // When another trip is selected: Very light
								0.5, // Default
							],
						},
					})

					// Add stop labels (sequence number)
					// In monochrome mode: halo color lightens with route
					const stopLabelHaloColor = [
						'case',
						['boolean', ['feature-state', 'selected'], false],
						'#1e3a8a', // Selected: Dark blue halo (matches selection color)
						['boolean', ['feature-state', 'hover'], false],
						'#1e3a8a',
						[
							'all',
							['boolean', ['feature-state', 'has_selection'], false],
							['!', ['boolean', ['feature-state', 'selected'], false]],
						],
						'#e2e8f0', // When another trip is selected: Very light gray
						colorMode === 'monochrome' ? '#bfdbfe' : color,
					]

					map.current!.addLayer({
						id: `${stopsSourceId}-labels`,
						type: 'symbol',
						source: stopsSourceId,
						minzoom: DETAIL_VIEW_ZOOM - 0.3,
						layout: {
							'text-field': ['get', 'sequence'],
							'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
							'text-size': 11,
							'text-offset': [0, -1.2],
							'text-anchor': 'top',
						},
						paint: {
							'text-color': '#fff',
							'text-halo-color': stopLabelHaloColor as any,
							'text-halo-width': 2,
							'text-opacity': [
								'case',
								['boolean', ['feature-state', 'selected'], false],
								1, // Selected: Solid
								['boolean', ['feature-state', 'hover'], false],
								1, // Hover: Solid
								[
									'all',
									['boolean', ['feature-state', 'has_selection'], false],
									['!', ['boolean', ['feature-state', 'selected'], false]],
								],
								0.2, // When another trip is selected: Very light
								1, // Default: Solid
							],
						},
					})

					tripInteractions.push({
						layerId: `${stopsSourceId}-dots`,
						events: [
							{
								type: 'mouseenter',
								handler: (event) => {
									if (map.current) {
										map.current.getCanvas().style.cursor = 'pointer'
										bringTripToTop()
										setRouteHover(true)
									}

									if (event.features && event.features.length > 0) {
										const feature = event.features[0]
										const props = feature.properties
										const shipmentStatus = props?.shipment_status || 'Unknown'
										let statusColor = '#6b7280'
										let statusIcon = '•'

										if (shipmentStatus === 'Delivered') {
											statusColor = '#22c55e'
											statusIcon = '✓'
										} else if (shipmentStatus === 'Picked Up') {
											statusColor = '#f59e0b'
											statusIcon = '→'
										} else if (shipmentStatus === 'At shipper') {
											statusColor = '#3b82f6'
											statusIcon = '○'
										} else if (shipmentStatus === 'Not Picked Up') {
											statusColor = '#ef4444'
											statusIcon = '○'
										}

										// Use reusable popup (Best Practice: Update content instead of creating new popups)
										if (map.current && popup.current) {
											popup.current
												.setLngLat(
													(feature.geometry as GeoJSON.Point).coordinates as [number, number]
												)
												.setHTML(
													`
							<div style="font-size: 12px;">
								<strong>PRO #${props?.pro_num}</strong><br/>
								<strong>Stop #${props?.sequence}</strong><br/>
								${props?.consignee_name || 'Unknown Location'}<br/>
								<span style="color: ${statusColor}; font-weight: 600;">${statusIcon} ${shipmentStatus}</span>
							</div>
						`
												)
												.addTo(map.current as any)
										}
									}
								},
							},
							{
								type: 'mouseleave',
								handler: () => {
									if (map.current) {
										map.current.getCanvas().style.cursor = ''
										removeTripFromTop()
										setRouteHover(false)
									}

									// Remove reusable popup (Best Practice: Reuse instead of destroying DOM)
									popup.current?.remove()
								},
							},
						],
					})
				}
			}

			// Ensure waypoints are always on top (highest z-index)
			// Move waypoint layers to the top after all other layers for this trip
			if (!isSinglePoint) {
				const waypointLayers = [
					`${sourceId}-pill`,
					`${sourceId}-point`,
					`${sourceId}-arrow`,
					`${sourceId}-label`,
					`${sourceId}-label-hover`,
				]

				waypointLayers.forEach((layerId) => {
					if (map.current!.getLayer(layerId)) {
						try {
							map.current!.moveLayer(layerId)
						} catch {
							// Layer might not exist, ignore
						}
					}
				})
			} else {
				// Single point trips: move point and label layers to top
				const singlePointLayers = [
					`${sourceId}-point`,
					`${sourceId}-label`,
					`${sourceId}-label-hover`,
				]

				singlePointLayers.forEach((layerId) => {
					if (map.current!.getLayer(layerId)) {
						try {
							map.current!.moveLayer(layerId)
						} catch {
							// Ignore
						}
					}
				})
			}

			if (tripInteractions.length) {
				registerInteractionSpec(tripInteractions)
			}
		})

		// DEPRECATED: Hover layers for line routes (disabled - breadcrumbs only)
		/*
		// Add all hover layers AFTER all base layers (ensures hovered route appears on top)
		if (colorMode === 'monochrome') {
			segmentsNeedingHoverLayers.forEach(({ sourceId }) => {
				// Hover line layer (dark, only visible on hover, renders on top of all base layers)
				map.current!.addLayer({
					id: `${sourceId}-line-hover`,
					type: 'line',
					source: sourceId,
					layout: {
						'line-cap': 'round',
						'line-join': 'round',
					},
					paint: {
						'line-color': '#1e40af', // Dark blue
						'line-width': 4,
						'line-opacity': [
							'case',
							['boolean', ['feature-state', 'hover'], false],
							1, // Show when hovered
							0, // Hide when not hovered
						],
						'line-dasharray': [1, 2.5],
					},
				})

				// Add hover events for the hover layer
				map.current!.on('mouseenter', `${sourceId}-line-hover`, () => {
					if (map.current) {
						map.current.getCanvas().style.cursor = 'pointer'
						map.current.setFeatureState({ source: sourceId, id: 0 }, { hover: true })
					}
				})

				map.current!.on('mouseleave', `${sourceId}-line-hover`, () => {
					if (map.current) {
						map.current.getCanvas().style.cursor = ''
						map.current.setFeatureState({ source: sourceId, id: 0 }, { hover: false })
					}
				})
			})
		}
		*/

		// Fit bounds on initial load or smoothly adjust on filter changes
		// Skip if there's a selected trip (let the selection effect handle zooming)
		const hasSelection = !!selectedTripIdRef.current

		if (segments.length && map.current && !hasSelection) {
			const bounds = new mapboxgl.LngLatBounds()
			segments.forEach((s) => {
				// Skip segments with missing or invalid bounds
				if (!s.bounds_json || !Array.isArray(s.bounds_json) || s.bounds_json.length < 2) return
				const [[w, s_lat], [e, n]] = s.bounds_json
				if (
					typeof w !== 'number' ||
					typeof s_lat !== 'number' ||
					typeof e !== 'number' ||
					typeof n !== 'number'
				)
					return
				bounds.extend([w, s_lat])
				bounds.extend([e, n])
			})

			if (!hasInitiallyFit.current) {
				// First load: instant fit
				map.current.fitBounds(bounds as any, {
					padding: 50,
					maxZoom: 12, // Prevent zooming too close (≈1 mile radius)
				})
				hasInitiallyFit.current = true
			} else {
				// Subsequent filter changes: smooth animation
				map.current.fitBounds(bounds as any, {
					padding: 50,
					duration: 1000, // 1 second smooth animation
					essential: true, // Animation completes even if user interacts
					maxZoom: 12, // Prevent zooming too close (≈1 mile radius)
				})
			}
		}
	}, [segments, mapLoaded, customIconLoaded, iconMode, colorMode, rawPings, snappingInProgress])

	return <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
}

function hashColor(str: string): string {
	let hash = 0
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash)
	}
	const hue = hash % 360
	return `hsl(${hue}, 70%, 50%)`
}

// Convert HSL to hex for icon IDs
function hslToHex(hsl: string): string {
	const match = hsl.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)
	if (!match) return hsl // Return as-is if not HSL format

	const h = parseInt(match[1]) / 360
	const s = parseInt(match[2]) / 100
	const l = parseInt(match[3]) / 100

	// Convert HSL to RGB
	const c = (1 - Math.abs(2 * l - 1)) * s
	const x = c * (1 - Math.abs(((h * 6) % 2) - 1))
	const m = l - c / 2

	let r = 0,
		g = 0,
		b = 0
	if (h < 1 / 6) {
		r = c
		g = x
		b = 0
	} else if (h < 2 / 6) {
		r = x
		g = c
		b = 0
	} else if (h < 3 / 6) {
		r = 0
		g = c
		b = x
	} else if (h < 4 / 6) {
		r = 0
		g = x
		b = c
	} else if (h < 5 / 6) {
		r = x
		g = 0
		b = c
	} else {
		r = c
		g = 0
		b = x
	}

	r = Math.round((r + m) * 255)
	g = Math.round((g + m) * 255)
	b = Math.round((b + m) * 255)

	return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

// Get segment color based on color mode
function getSegmentColor(segment: TripSegment, colorMode: ColorMode): string {
	switch (colorMode) {
		case 'multicolor':
			return hashColor(segment.trip_id)
		case 'monochrome':
			return '#1e40af' // Dark blue
		case 'status':
			// Color based on delivery status
			if (segment.order_status === 'D' || segment.shipment_status === 'Delivered') {
				return '#22c55e' // Green for delivered
			} else if (!segment.is_active) {
				return '#6b7280' // Gray for inactive
			} else {
				return '#3b82f6' // Blue for active/in-transit
			}
		default:
			return hashColor(segment.trip_id)
	}
}

// Find the last two distinct coordinates (using distance threshold) to avoid false "stopped" when duplicates
function distanceBetweenCoords(c1: [number, number], c2: [number, number]): number {
	const R = 6371e3 // metres
	const φ1 = (c1[1] * Math.PI) / 180
	const φ2 = (c2[1] * Math.PI) / 180
	const Δφ = ((c2[1] - c1[1]) * Math.PI) / 180
	const Δλ = ((c2[0] - c1[0]) * Math.PI) / 180

	const a =
		Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
		Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
	return R * c
}

function resolveMarkerCoordinates(
	pings: RawPing[],
	segment: TripSegment
): { lastCoord: [number, number]; secondLastCoord: [number, number] } {
	const fromPings: Array<[number, number]> =
		pings.length > 0 ? pings.map((p) => [p.lng, p.lat] as [number, number]) : []

	// Handle missing or invalid snapped_geojson
	let baseCoords: Array<[number, number]> = []
	if (segment.snapped_geojson?.coordinates) {
		baseCoords = (
			segment.snapped_geojson.type === 'LineString'
				? segment.snapped_geojson.coordinates
				: segment.snapped_geojson.type === 'Point'
					? [segment.snapped_geojson.coordinates]
					: (segment.snapped_geojson as GeoJSON.MultiLineString).coordinates.flat()
		) as Array<[number, number]>
	}

	const sourceCoords = fromPings.length ? fromPings : (baseCoords as Array<[number, number]>)

	if (sourceCoords.length === 0) {
		return {
			lastCoord: [0, 0],
			secondLastCoord: [0, 0],
		}
	}

	const lastCoord = sourceCoords[sourceCoords.length - 1]
	let secondLastCoord = lastCoord

	for (let i = sourceCoords.length - 2; i >= 0; i -= 1) {
		const candidate = sourceCoords[i]
		const distance = distanceBetweenCoords(candidate, lastCoord)
		if (distance >= 15) {
			secondLastCoord = candidate
			break
		}
	}

	return { lastCoord, secondLastCoord }
}

function calculateBearing(from: number[], to: number[]): number {
	const [lon1, lat1] = from
	const [lon2, lat2] = to

	const dLon = ((lon2 - lon1) * Math.PI) / 180
	const lat1Rad = (lat1 * Math.PI) / 180
	const lat2Rad = (lat2 * Math.PI) / 180

	const y = Math.sin(dLon) * Math.cos(lat2Rad)
	const x =
		Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon)

	let bearing = (Math.atan2(y, x) * 180) / Math.PI
	bearing = (bearing + 360) % 360

	return bearing
}

function getContrastColor(hslColor: string): string {
	// Parse HSL color string like "hsl(120, 70%, 50%)"
	const match = hslColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)
	if (!match) return '#ffffff'

	const h = parseInt(match[1])
	const s = parseInt(match[2]) / 100
	const l = parseInt(match[3]) / 100

	// Convert HSL to RGB for luminance calculation
	const c = (1 - Math.abs(2 * l - 1)) * s
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
	const m = l - c / 2

	let r = 0,
		g = 0,
		b = 0
	if (h >= 0 && h < 60) {
		r = c
		g = x
		b = 0
	} else if (h >= 60 && h < 120) {
		r = x
		g = c
		b = 0
	} else if (h >= 120 && h < 180) {
		r = 0
		g = c
		b = x
	} else if (h >= 180 && h < 240) {
		r = 0
		g = x
		b = c
	} else if (h >= 240 && h < 300) {
		r = x
		g = 0
		b = c
	} else {
		r = c
		g = 0
		b = x
	}

	r = (r + m) * 255
	g = (g + m) * 255
	b = (b + m) * 255

	// Calculate relative luminance using sRGB coefficients
	const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b

	// Return white for darker colors, black for lighter colors
	// Increased threshold to 165 to prefer white text more often (better for blues/purples)
	return luminance < 165 ? '#ffffff' : '#000000'
}
